import { Box, Checkbox, styled, TextField, TextFieldProps, useTheme } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import get from 'lodash/get';
import moment from 'moment';
import React from 'react';
import {
  Control,
  Controller,
  FormState,
  Path,
  UseFormRegister,
  UseFormWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { COLORS } from '../../../lib/colors';
import { UserLogin, UserRegistration } from '../../../lib/types';
import {
  has1Lowercase,
  has1Number,
  has1Uppercase,
  hasAtleastNCharacters,
  isValidEmail,
} from '../../../lib/validators';
import { Caption } from '../../typography/Caption';

// Allowing any in certain cases:
// Disable the type errors for body2 caused by the use of mui/labs alpha having looser types than mui/system:
//  Type 'CSSProperties' is not assignable to type 'Properties<string | number, string & {}>'

interface FormProps<T extends UserLogin = UserRegistration> {
  register: UseFormRegister<T>;
  formState: FormState<T>;
  watch: UseFormWatch<T>;
  control?: Control<T>;
}

interface EmailFormProps<T extends UserLogin = UserRegistration> extends FormProps<T> {
  autofillFields?: Partial<T>;
}

interface ControlProps {
  control: Control<UserRegistration, unknown>;
}

const AgreementCaption = styled(Caption)({
  textAlign: 'left',
  fontSize: '1.4rem',
  lineHeight: 1.6,
  marginLeft: 4,
  paddingTop: 16,
});

const StyledTextField = styled(TextField)<TextFieldProps>(({ theme }) => ({
  '& .MuiInput-underline:before': {
    borderBottomColor: theme.palette.divider,
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
  },
}));

const CheckboxAnswer = styled(Checkbox)(({ theme }) => ({
  marginRight: 0,
  marginLeft: 0,
  '& .MuiSvgIcon-root': { fontSize: 24 },
  borderRadius: '100px',
  fontSize: '1.9rem',
  '&&:disabled': {
    color: theme.palette.primary.contrastText,
  },
  '&&:hover': {
    backgroundColor: COLORS.O4M_LIGHT_CREAM,
    borderColor: theme.palette.secondary.light,
    boxShadow: 'none',
  },
  '&&:active': {
    backgroundColor: COLORS.O4M_LIGHT_CREAM,
    borderColor: theme.palette.secondary.dark,
    boxShadow: 'none',
  },
}));

export const NameField: React.FC<FormProps> = ({ register, formState, watch }) => {
  const { t } = useTranslation('signUp');
  const theme = useTheme();
  return (
    <StyledTextField
      fullWidth
      id="name"
      label={t('name', 'Name')}
      variant="outlined"
      autoComplete="name"
      error={Boolean(formState.errors?.name)}
      // Disable the following error caused by the use of mui/labs alpha having looser types than mui/system:
      //  Type 'CSSProperties' is not assignable to type 'Properties<string | number, string & {}>'
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      InputProps={{ style: theme.typography.body2 } as { style: any }}
      // forces the placeholder label to shrink up so it doesn't overlap with input
      InputLabelProps={{
        // Disable the following error caused by the use of mui/labs alpha having looser types than mui/system:
        //  Type 'CSSProperties' is not assignable to type 'Properties<string | number, string & {}>'
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
        style: theme.typography.body2 as any,
        shrink: !!watch('name'),
      }}
      helperText={<span>{get(formState, 'errors.name.message', '')}</span>}
      {...register('name', {
        required: t('Name is required', 'Name is required.'),
      })}
    />
  );
};

export function EmailField<T extends UserLogin = UserRegistration>({
  register,
  formState,
  autofillFields,
  watch,
}: EmailFormProps<T>) {
  const { t } = useTranslation('signUp');
  const theme = useTheme();
  return (
    <StyledTextField
      fullWidth
      id="email"
      label={t('email', 'Email')}
      type="email"
      variant="outlined"
      autoComplete="username"
      defaultValue={autofillFields?.email}
      error={Boolean(formState.errors?.email)}
      helperText={<span>{get(formState, 'errors.email.message', '')}</span>}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
      InputProps={{ style: theme.typography.body2 as any }}
      // forces the placeholder label to shrink up so it doesn't overlap with input
      InputLabelProps={{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
        style: theme.typography.body2 as any,
        shrink: !!watch('email' as Path<T>),
      }}
      {...register('email' as Path<T>, {
        required: t('Email is required', 'Email is required.'),
        validate: {
          isValidEmail,
        },
      })}
    />
  );
}

export function PasswordField<T extends UserLogin = UserRegistration>({
  register,
  formState,
  validate,
  watch,
}: FormProps<T> & { validate?: boolean }) {
  const { t } = useTranslation('signUp');
  const theme = useTheme();
  return (
    <StyledTextField
      fullWidth
      id="password"
      label={t('password', 'Password')}
      type="password"
      variant="outlined"
      autoComplete="new-password"
      error={Boolean(formState.errors?.password)}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
      InputProps={{ style: theme.typography.body2 as any }}
      // forces the placeholder label to shrink up so it doesn't overlap with input

      InputLabelProps={{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
        style: theme.typography.body2 as any,
        shrink: !!watch('password' as Path<T>),
      }}
      {...register('password' as Path<T>, {
        required: t('Password is required', 'Password is required.'),
        validate: validate
          ? {
              hasAtleast8Characters: (val) => hasAtleastNCharacters(val, 8),
              has1Number,
              has1Lowercase,
              has1Uppercase,
            }
          : undefined,
      })}
    />
  );
}

export const DateOfBirthField: React.FC<FormProps> = ({ control }) => {
  const { t } = useTranslation('signUp');

  if (!control) {
    throw new Error('DateOfBirthField requires control prop');
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Controller
        control={control}
        name="dateOfBirth"
        rules={{
          required: t('Date of birth is required', 'Date of birth is required.'),
          validate: {
            isAdult: (val) => {
              if (!val) return true; // Let required validation handle empty values
              const birthDate = moment(val).toDate();
              const today = new Date();
              const age = today.getFullYear() - birthDate.getFullYear();
              const monthDiff = today.getMonth() - birthDate.getMonth();
              const dayDiff = today.getDate() - birthDate.getDate();
              const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;
              return actualAge >= 18 || t('You must be over 18', 'You must be over 18.');
            },
          },
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <DatePicker
            label={t('date of birth', 'Date of birth')}
            value={value ? moment(value) : null}
            onChange={(date) => onChange(date?.format('YYYY-MM-DD') || '')}
            minDate={moment('01-01-1900', 'MM-DD-YYYY')}
            maxDate={moment().subtract(18, 'years')}
            slotProps={{
              textField: {
                fullWidth: true,
                id: 'dateOfBirth',
                variant: 'outlined',
                autoComplete: 'bday',
                error: Boolean(error),
                helperText: error ? <span>{error.message}</span> : undefined,
              },
              openPickerIcon: {
                sx: {
                  fontSize: '2.4rem',
                  width: '2.4rem',
                  height: '2.4rem',
                },
              },
            }}
          />
        )}
      />
    </LocalizationProvider>
  );
};

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyItems: 'center',
  marginBottom: '8px',
}));

export const TermsOfServiceAgreeController: React.FC<ControlProps> = ({ control }) => {
  const { t } = useTranslation('signUp');
  return (
    <StyledBox>
      <Controller
        control={control}
        name="accepted_terms_of_service"
        rules={{ required: true }}
        defaultValue={false}
        render={({ field: { onChange, value } }) => (
          <CheckboxAnswer color="primary" onChange={onChange} checked={value as boolean} />
        )}
      />
      <AgreementCaption>
        {t(
          'agreeTermsOfService',
          `I agree to Outcomes4Me's Terms of Service, Privacy Policy, and Consumer Health Data Policy.`,
        )}
      </AgreementCaption>
    </StyledBox>
  );
};

export const SponsoredContentAgreeController: React.FC<ControlProps> = ({ control }) => {
  const { t } = useTranslation('signUp');
  return (
    <StyledBox>
      <Controller
        control={control}
        name="accepted_sponsored_content"
        rules={{ required: true }}
        defaultValue={false}
        render={({ field: { onChange, value } }) => (
          <CheckboxAnswer color="primary" onChange={onChange} checked={value as boolean} />
        )}
      />
      <AgreementCaption>
        {t(
          'agreeSponsoredContent',
          `I agree to receive sponsored information about clinical trials and treatments relevant to my condition including by email.`,
        )}
      </AgreementCaption>
    </StyledBox>
  );
};
